import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { addDays, mondayOf, toISO } from "@/lib/dateUtils";
import { PublicAgendaGrid, PublicMobileDay } from "@/components/AgendaGrid";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

function useIsMobile() {
    const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth < 768);
    useEffect(() => {
        const on = () => setM(window.innerWidth < 768);
        window.addEventListener("resize", on);
        return () => window.removeEventListener("resize", on);
    }, []);
    return m;
}

export default function PublicAgenda() {
    const isMobile = useIsMobile();
    const [monday, setMonday] = useState(() => mondayOf(new Date()));
    const [week, setWeek] = useState(null);
    const [selection, setSelection] = useState(null); // { date, startHour }
    const [dayIndex, setDayIndex] = useState(() => Math.min(5, (new Date().getDay() + 6) % 7));
    const [showForm, setShowForm] = useState(false);
    const [confirmData, setConfirmData] = useState(null);

    const loadWeek = async (mDate) => {
        try {
            const { data } = await api.get(`/public/week`, { params: { date: toISO(mDate) } });
            setWeek(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { loadWeek(monday); }, [monday]);

    const shiftWeek = (n) => {
        setSelection(null);
        setMonday(addDays(monday, n * 7));
    };

    const onCellClick = (day, cell) => {
        if (cell.status !== "available") return;
        const sm = cell.start_minute || 0;
        setSelection({ date: day.date, startHour: cell.hour, startMinute: sm });
    };

    const selectedDay = week?.days?.find((d) => d.date === selection?.date);
    const endHM = selection ? (selection.startMinute === 30 ? [selection.startHour + 2, 30] : [selection.startHour + 2, 0]) : null;
    const selectedLabel = selection && selectedDay
        ? `${selectedDay.weekday_name} ${selectedDay.date_label} • ${String(selection.startHour).padStart(2, "0")}:${String(selection.startMinute).padStart(2,"0")} – ${String(endHM[0]).padStart(2, "0")}:${String(endHM[1]).padStart(2,"0")}`
        : "";

    return (
        <div className="min-h-screen bg-white">
            <div className="max-w-5xl mx-auto px-4 py-6">
                <header className="mb-6 text-center">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight" data-testid="app-title">MAX AGENDA</h1>
                    <p className="text-sm md:text-base text-gray-600 mt-1">Prenota la tua consulenza con {week?.display_name || "Max Gentile"}</p>
                </header>

                <div className="flex items-center justify-between mb-4 border-y border-gray-200 py-3">
                    <button className="p-2 text-gray-700 hover:text-gray-900" onClick={() => shiftWeek(-1)} data-testid="prev-week-btn" aria-label="Settimana precedente">
                        <ChevronLeft size={22} />
                    </button>
                    <div className="text-sm md:text-base font-semibold text-gray-900" data-testid="week-label">{week?.week_label || "…"}</div>
                    <button className="p-2 text-gray-700 hover:text-gray-900" onClick={() => shiftWeek(1)} data-testid="next-week-btn" aria-label="Settimana successiva">
                        <ChevronRight size={22} />
                    </button>
                </div>

                {week && (isMobile ? (
                    <PublicMobileDay weekData={week} dayIndex={dayIndex} setDayIndex={setDayIndex} selection={selection} onCellClick={onCellClick} />
                ) : (
                    <PublicAgendaGrid weekData={week} selection={selection} onCellClick={onCellClick} />
                ))}

                <div className="mt-6 flex flex-col items-center gap-3">
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                        <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 border border-gray-300" style={{ backgroundColor: "#22C55E" }}></span>Disponibile</div>
                        <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 border border-gray-300" style={{ backgroundColor: "#F3F4F6" }}></span>Non disponibile</div>
                    </div>
                    {selection && (
                        <div className="w-full max-w-md border border-gray-200 bg-gray-50 p-4 text-center">
                            <div className="text-sm text-gray-700">Consulenza selezionata:</div>
                            <div className="font-bold text-gray-900 text-base my-1" data-testid="selection-label">{selectedLabel}</div>
                            <div className="text-xs text-gray-500 mb-3">Durata: 2 ore</div>
                            <Button className="bg-green-700 hover:bg-green-800 text-white" onClick={() => setShowForm(true)} data-testid="continue-btn">
                                CONTINUA
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <BookingFormDialog
                open={showForm}
                onOpenChange={setShowForm}
                selection={selection}
                selectedDay={selectedDay}
                onSuccess={(data) => {
                    setShowForm(false);
                    setSelection(null);
                    setConfirmData(data);
                    loadWeek(monday);
                }}
            />

            <ConfirmationDialog open={!!confirmData} onOpenChange={(v) => !v && setConfirmData(null)} data={confirmData} />

            <div className="text-center pb-6">
                <a href="/admin/login" className="text-xs text-gray-400 hover:text-gray-600" data-testid="admin-link">Area riservata</a>
            </div>
        </div>
    );
}

function BookingFormDialog({ open, onOpenChange, selection, selectedDay, onSuccess }) {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "", privacy: false });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) setForm({ first_name: "", last_name: "", email: "", phone: "", notes: "", privacy: false });
    }, [open]);

    if (!selection || !selectedDay) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.privacy) { toast.error("Devi accettare il trattamento dei dati personali"); return; }
        if (!form.first_name || !form.last_name || !form.email || !form.phone) { toast.error("Compila tutti i campi obbligatori"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/public/bookings", {
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email,
                phone: form.phone,
                notes: form.notes,
                date: selection.date,
                start_hour: selection.startHour,
                start_minute: selection.startMinute || 0,
                privacy_accepted: form.privacy,
            });
            onSuccess(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    const timeLabel = `${String(selection.startHour).padStart(2, "0")}:${String(selection.startMinute||0).padStart(2,"0")} – ${String(selection.startMinute===30 ? selection.startHour+2 : selection.startHour+2).padStart(2, "0")}:${String(selection.startMinute===30?30:0).padStart(2,"0")}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md w-[calc(100vw-1rem)] p-4 sm:p-6 max-h-[90dvh] max-h-[90vh] overflow-y-auto booking-modal-scroll top-[5dvh] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]"
                data-testid="booking-form-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Conferma prenotazione</DialogTitle>
                    <DialogDescription>Compila i tuoi dati per confermare la consulenza.</DialogDescription>
                </DialogHeader>
                <div className="border border-gray-200 bg-gray-50 p-3 mb-3 text-sm">
                    <div><span className="text-gray-500">Giorno:</span> <b>{selectedDay.weekday_name}</b></div>
                    <div><span className="text-gray-500">Data:</span> <b>{selectedDay.date_label}</b></div>
                    <div><span className="text-gray-500">Orario:</span> <b>{timeLabel}</b></div>
                    <div><span className="text-gray-500">Durata:</span> <b>2 ore</b></div>
                </div>
                <form onSubmit={submit} className="space-y-3 pb-[120px] sm:pb-2">
                    <div>
                        <Label htmlFor="fn">Nome *</Label>
                        <Input id="fn" data-testid="form-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
                    </div>
                    <div>
                        <Label htmlFor="ln">Cognome *</Label>
                        <Input id="ln" data-testid="form-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
                    </div>
                    <div>
                        <Label htmlFor="em">Email *</Label>
                        <Input id="em" type="email" data-testid="form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    </div>
                    <div>
                        <Label htmlFor="ph">Telefono *</Label>
                        <Input id="ph" data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                    </div>
                    <div>
                        <Label htmlFor="nt">Note</Label>
                        <Textarea id="nt" data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                    </div>
                    <div className="flex items-start gap-2">
                        <Checkbox id="pr" data-testid="form-privacy" checked={form.privacy} onCheckedChange={(v) => setForm({ ...form, privacy: !!v })} />
                        <Label htmlFor="pr" className="text-xs font-normal leading-snug">Acconsento al trattamento dei dati personali per la finalità di gestione dell'appuntamento. *</Label>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-green-700 hover:bg-green-800 text-white" data-testid="submit-booking-btn">
                        {loading ? "Invio…" : "CONFERMA PRENOTAZIONE"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ConfirmationDialog({ open, onOpenChange, data }) {
    if (!data) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md w-[calc(100vw-1rem)] p-4 sm:p-6 max-h-[90dvh] max-h-[90vh] overflow-y-auto booking-modal-scroll top-[5dvh] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]"
                data-testid="confirmation-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-green-700">Prenotazione confermata</DialogTitle>
                    <DialogDescription>La tua consulenza con Max Gentile è stata prenotata.</DialogDescription>
                </DialogHeader>
                <div className="border border-gray-200 bg-gray-50 p-3 text-sm space-y-1">
                    <div><span className="text-gray-500">Giorno:</span> <b>{data.weekday_name}</b></div>
                    <div><span className="text-gray-500">Data:</span> <b>{data.date_label}</b></div>
                    <div><span className="text-gray-500">Orario:</span> <b>{data.time_label}</b></div>
                    <div><span className="text-gray-500">Nome:</span> <b>{data.first_name} {data.last_name}</b></div>
                    <div><span className="text-gray-500">Email:</span> <b>{data.email}</b></div>
                </div>
                <Button className="w-full" onClick={() => onOpenChange(false)} data-testid="close-confirmation-btn">Chiudi</Button>
            </DialogContent>
        </Dialog>
    );
}
